import React, { useEffect, useRef, useState } from "react";
import ProgressSteps from "../components/ProgressSteps";
import Step1PersonalInfo from "../steps/Step1PersonalInfo";
import Step2VehicleInfo from "../steps/Step2VehicleInfo";
import Step3PaymentInfo from "../steps/Step3PaymentInfo";
import Step4TransferInfo from "../steps/Step4TransferInfo";
import Step5ConditionInfo from "../steps/Step5ConditionInfo";
import Step6DamageInfo from "../steps/Step6DamageInfo";
import Step7Signature from "../steps/Step7Signature";
import { createContract } from "../api/contractApi";
import { getStoredProfileSignature } from "../utils/profileSignature";
import { applyProfilePersonalInfo } from "../utils/profilePersonalInfo";
import { getCurrentUser } from "../api/authApi";
import {
  buildContractPayload,
  shouldRequireSignature,
} from "../utils/contractSubmission";

export const INITIAL_DATA = {
  firstName: "",
  lastName: "",
  fatherName: "",
  idNumber: "",
  nationalCode: "",
  idIssuePlace: "",
  birthDate: "",
  address: "",
  phone: "",
  mobile: "",
  vehicleName: "",
  vehicleModel: "",
  vehicleColor: "",
  chassisNumber: "",
  engineNumber: "",
  plateNumber: "",
  amountToman: "",
  amountRial: "",
  paymentTerms: "",
  dealDescription: "",
  transferFeeBy: "",
  plateFeeBy: "",
  notaryDate: "",
  notaryNumber: "",
  notaryAddress: "",
  deliveryDate: "",
  deliveryTime: "",
  vehicleCondition: "",
  vehicleMileage: "",
  penaltyDescription: "",
  signature: null,
  buyerFirstName: "",
  buyerLastName: "",
  buyerFatherName: "",
  buyerIdNumber: "",
  buyerNationalCode: "",
  buyerIdIssuePlace: "",
  buyerBirthDate: "",
  buyerAddress: "",
  buyerPhone: "",
  buyerMobile: "",
  buyerSignature: null,
};

const STORAGE_KEY = "qolnameh_seller_draft";

const STEPS = [
  { title: "اطلاعات هویتی", component: Step1PersonalInfo },
  { title: "اطلاعات خودرو", component: Step2VehicleInfo },
  { title: "مبلغ و شرایط", component: Step3PaymentInfo },
  { title: "نقل و انتقال", component: Step4TransferInfo },
  { title: "وضعیت تحویل", component: Step5ConditionInfo },
  { title: "ضرر و زیان", component: Step6DamageInfo },
  { title: "بازبینی و امضا", component: Step7Signature },
];

export default function SellerWizard({
  onComplete,
  onBackToRoles,
  flowType = "seller",
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const isSinglePersonMode = flowType === "single-person";
  const [formData, setFormData] = useState(() => {
    const storedSignature = getStoredProfileSignature();
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const savedData = saved ? JSON.parse(saved) : {};
      return {
        ...INITIAL_DATA,
        ...savedData,
        signature: savedData.signature || storedSignature,
      };
    } catch {
      return { ...INITIAL_DATA, signature: storedSignature };
    }
  });

  const buyerIdentityFields = isSinglePersonMode
    ? {
        buyerFirstName: formData.buyerFirstName || "",
        buyerLastName: formData.buyerLastName || "",
        buyerFatherName: formData.buyerFatherName || "",
        buyerIdNumber: formData.buyerIdNumber || "",
        buyerNationalCode: formData.buyerNationalCode || "",
        buyerIdIssuePlace: formData.buyerIdIssuePlace || "",
        buyerBirthDate: formData.buyerBirthDate || "",
        buyerAddress: formData.buyerAddress || "",
        buyerPhone: formData.buyerPhone || "",
        buyerMobile: formData.buyerMobile || "",
      }
    : {};

  useEffect(() => {
    let active = true;

    getCurrentUser()
      .then((user) => {
        if (!active) return;
        setFormData((prev) => applyProfilePersonalInfo({ ...prev }, user));
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const formRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignatureChange = (dataUrl) => {
    setFormData((prev) => ({ ...prev, signature: dataUrl }));
  };

  const isLastStep = currentStep === STEPS.length;
  const isFirstStep = currentStep === 1;

  const handleFinalSubmit = async () => {
    if (shouldRequireSignature(flowType) && !formData.signature) {
      window.alert("لطفاً پیش از ثبت نهایی، امضای خود را وارد کنید.");
      return;
    }
    if (
      !isSinglePersonMode &&
      (!formData.buyerMobile || formData.buyerMobile.trim().length < 10)
    ) {
      window.alert("لطفاً شماره موبایل خریدار را وارد کنید.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const payload = buildContractPayload(
        {
          ...formData,
          ...buyerIdentityFields,
        },
        flowType,
      );

      const data = await createContract(payload);
      const contractId =
        data?._id ||
        data?.id ||
        data?.data?._id ||
        data?.data?.id ||
        data?.contract?._id;

      if (!contractId) throw new Error("شناسه قولنامه از سرور دریافت نشد.");

      localStorage.removeItem(STORAGE_KEY);
      onComplete({ ...formData, contractId, flowType });
    } catch (err) {
      setSubmitError(err.message || "خطا در ارتباط با سرور. دوباره تلاش کنید.");
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (formRef.current && !formRef.current.reportValidity()) return;
    if (isLastStep) {
      handleFinalSubmit();
      return;
    }
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
  };

  const goBack = () => {
    if (isFirstStep) {
      onBackToRoles();
      return;
    }
    setCurrentStep((s) => Math.max(s - 1, 1));
  };

  console.log("Current Step:", currentStep);

  const CurrentStepComponent = STEPS[currentStep - 1].component;

  return (
    <div className="min-h-screen bg-surface px-4 py-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-lg text-white">
            🚗
          </div>
          <div>
            <h1 className="text-base font-extrabold text-navy sm:text-lg">
              قولنامه هوشمند — فروشنده
            </h1>
            <p className="text-xs text-gray-500">قرارداد ۲۴</p>
          </div>
        </div>

        <div className="mb-6 rounded-2xl bg-white p-4 shadow-card sm:p-5">
          <ProgressSteps steps={STEPS} currentStep={currentStep} />
        </div>

        <form
          ref={formRef}
          onSubmit={(e) => e.preventDefault()}
          className="step-enter rounded-2xl bg-white p-5 shadow-card sm:p-8"
        >
          <CurrentStepComponent
            data={formData}
            onChange={handleChange}
            onSignatureChange={handleSignatureChange}
            flowType={flowType}
          />

          {/* فیلد موبایل خریدار — فقط در آخرین مرحله و فقط در حالت عادی */}
          {isLastStep && !isSinglePersonMode && (
            <div className="mt-6 border-t border-gray-100 pt-6">
              <h3 className="mb-1 text-sm font-bold text-navy">
                ارسال قولنامه به خریدار
              </h3>
              <p className="mb-3 text-xs text-gray-500">
                بعد از ثبت، یک لینک اختصاصی به این شماره پیامک می‌شود تا خریدار
                اطلاعات و امضای خود را تکمیل کند.
              </p>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg">
                    📱
                  </span>
                  <input
                    type="tel"
                    name="buyerMobile"
                    value={formData.buyerMobile}
                    onChange={handleChange}
                    placeholder="شماره موبایل خریدار — مثلاً ۰۹۱۲۱۲۳۴۵۶۷"
                    className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-4 pr-10 text-sm text-navy placeholder:text-gray-400 outline-none transition focus:border-sky focus:ring-2 focus:ring-sky-light/50"
                  />
                </div>
              </div>
            </div>
          )}

          {isLastStep && isSinglePersonMode && (
            <div className="mt-6 border-t border-gray-100 pt-6">
              <h3 className="mb-1 text-sm font-bold text-navy">ثبت یک‌نفره</h3>
              <p className="mb-3 text-xs text-gray-500">
                در این حالت همه اطلاعات توسط یک نفر وارد می‌شود و امضای شما
                اختیاری است.
              </p>
            </div>
          )}

          {submitError && (
            <p className="mt-3 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600">
              ⚠️ {submitError}
            </p>
          )}
        </form>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={goBack}
            disabled={submitting}
            className="rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-sm font-bold text-navy transition hover:border-sky-light disabled:opacity-50"
          >
            {isFirstStep ? "بازگشت به انتخاب نقش" : "مرحله قبل"}
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={submitting}
            className="flex items-center gap-2 rounded-xl bg-sky px-8 py-2.5 text-sm font-bold text-white shadow-card transition hover:bg-sky-dark disabled:opacity-60"
          >
            {submitting && (
              <svg
                className="h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
            )}
            {submitting
              ? "در حال ثبت..."
              : isLastStep
                ? "ثبت و ارسال به خریدار"
                : "مرحله بعد"}
          </button>
        </div>
      </div>
    </div>
  );
}
